/*
 * Copyright (c) 2025 curryfirm.com
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Link, Stack, Button } from '@mui/material';
import {
    selectConfirmedDumpDetails,
    selectEmptyAvailableDumpsterDetails,
    selectStartLocationDetails,
    selectEndLocationDetails,
    selectChosenDumpsterDetails,
} from '../../../features/workflow/workflowSelectors.js';
import { dumpsterToString } from '../../../services/helpers.js';
import {selectStoredRouteType, WIZARD_STEPS} from "../../../features/workflow/workflowSlice.js";
import {ROUTE_TYPES} from "../../../constants.js";


const DisplaySummarySections = ({ summarySections }) => {
    return (
        <Stack spacing={3}>
            {summarySections.map(({ label, value, onEdit }, idx) => (
                <Box
                    key={idx}
                    sx={{
                        position: 'relative',
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 2,
                        bgcolor: 'background.paper',
                        minHeight: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h6" color="text.secondary" mb={1}>
                        {label}
                    </Typography>
                    <Box>
                        {value.map((str, i) => (
                            <Typography key={i} variant="body1">
                                {str}
                            </Typography>
                        ))}
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 16,
                            bottom: 16,
                        }}
                    >
                        <Link
                            component="button"
                            underline="always"
                            sx={{ color: 'primary.main', fontWeight: 500, cursor: 'pointer' }}
                            onClick={onEdit}
                        >
                            Edit
                        </Link>
                    </Box>
                </Box>
            ))}
        </Stack>
    )
}

const WizardSummary = ({ onBack, onContinue, editHandlers }) => {
    const storedRouteType = useSelector(selectStoredRouteType);
    const routeTypeRow = [ storedRouteType ];
    const isSwapAndDrop = (storedRouteType === ROUTE_TYPES.SWAP_AND_DROP);
    const dumps = useSelector(selectConfirmedDumpDetails);
    const dumpRows = dumps.map(d => `${d.dumpName}: ${d.street}`);
    const emptyAvailableCanObjects = useSelector(selectEmptyAvailableDumpsterDetails);
    const emptyAvailableCanRows = emptyAvailableCanObjects.map(d => `${d.dumpsterId}: ${d.street}`);
    const startLocationObject = useSelector(selectStartLocationDetails);
    const startLocationRows = [ `${startLocationObject.customer}: ${startLocationObject.street}, ${startLocationObject.city}` ];
    const endLocationObject = useSelector(selectEndLocationDetails);
    const endLocationRows = [ `${endLocationObject.customer}: ${endLocationObject.street}, ${endLocationObject.city}` ];
    const chosenDumpsterObjects = useSelector(selectChosenDumpsterDetails);
    const chosenDumpsterRows = chosenDumpsterObjects.map(d => dumpsterToString(d));
    const baseSections = [
        { label: 'Route Type', value: routeTypeRow, onEdit: editHandlers.editRouteType },
        { label: 'Dumps to Consider', value: dumpRows, onEdit: editHandlers.editDumps },
        { label: 'Empty Can to Use', value: emptyAvailableCanRows, onEdit: editHandlers.editEmptyCanInventory },
        { label: 'Start Location', value: startLocationRows, onEdit: editHandlers.editStartingLocation },
        { label: 'End Location', value: endLocationRows, onEdit: editHandlers.editEndingLocation },
        { label: 'Dumpsters To Haul', value: chosenDumpsterRows , onEdit: editHandlers.editDumpsterSelection },
    ];
    const summarySections = isSwapAndDrop
        ? baseSections
        : baseSections.filter(section => section.label !== 'Empty Can to Use');

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Review and Confirm</Typography>
            <DisplaySummarySections summarySections={summarySections} />
            <Stack direction="row" spacing={2} sx={{ mt: 4, justifyContent: 'flex-end' }}>
                <Button variant="contained" color="primary" onClick={onBack}>Back</Button>
                <Button variant="contained" color="primary" onClick={onContinue}>Compute Route</Button>
            </Stack>
        </Box>
    );
};

export default WizardSummary;
